/**
 * Tile Palette panel (engine 46.2, React edition). Shows the active layer's
 * tileset atlas as a zoomable grid canvas: click selects one tile, drag selects
 * a rectangular multi-tile stamp (the store's `stamp`). A metadata strip edits
 * the selected tile's Solid/Tag, written through to the tileset via the
 * tile-meta partial-merge endpoint. A "New Tileset" button opens the dialog.
 *
 * The atlas is drawn from the SAME slicing function the renderer uses
 * (TilesetImage.tileRectPx) — one source of truth for geometry (engine 46.2).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTilemapStore, tilemapStore } from '../store/tilemapStore';
import { TilesetImage } from '../surface/tileset';
import { tilemapClient } from '../api/tilemapClient';
import type { Stamp, Tileset } from '../model/types';
import { NewTilesetDialog } from './NewTilesetDialog';

const ORANGE = '#F5793B';

export function TilePalettePanel({ projectUuid }: { projectUuid: string }) {
    const doc = useTilemapStore((s) => s.doc);
    const activeLayerUuid = useTilemapStore((s) => s.activeLayerUuid);
    const stamp = useTilemapStore((s) => s.stamp);

    const [showNew, setShowNew] = useState(false);

    // The tileset the active layer paints from.
    const tileset = useMemo<Tileset | null>(() => {
        if (!doc) return null;
        const layer = doc.layers.find((l) => l.uuid === activeLayerUuid);
        return doc.tilesets.find((t) => t.uuid === layer?.tileset_uuid) ?? null;
    }, [doc, activeLayerUuid]);

    if (!doc) return null;

    return (
        <div className="d-flex flex-column h-100">
            <div className="d-flex align-items-center gap-2 p-2 border-bottom">
                <span className="fw-bold small">Tile Palette</span>
                <button className="btn btn-sm btn-outline-primary ms-auto" onClick={() => setShowNew(true)}>
                    <i className="ti ti-plus" /> New Tileset
                </button>
            </div>

            {tileset ? (
                <PaletteGrid projectUuid={projectUuid} tileset={tileset} stamp={stamp} />
            ) : (
                <div className="p-3 text-secondary small">
                    The active layer has no tileset. Create one, then add a layer that uses it.
                </div>
            )}

            {showNew && (
                <NewTilesetDialog
                    projectUuid={projectUuid}
                    onClose={() => setShowNew(false)}
                    onCreated={async () => {
                        setShowNew(false);
                        await tilemapStore.getState().reload();
                    }}
                />
            )}
        </div>
    );
}

function PaletteGrid({ projectUuid, tileset, stamp }: { projectUuid: string; tileset: Tileset; stamp: Stamp }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [img, setImg] = useState<TilesetImage | null>(null);
    const [zoom, setZoom] = useState(2);
    const [sel, setSel] = useState<{ c0: number; r0: number; c1: number; r1: number } | null>(null);
    const dragging = useRef(false);

    const cols = tileset.columns ?? 1;
    const rows = tileset.tile_count && cols ? Math.ceil(tileset.tile_count / cols) : 1;
    const tw = tileset.tile_width;
    const th = tileset.tile_height;

    // Load the atlas image.
    useEffect(() => {
        let alive = true;
        TilesetImage.load(tileset).then((t) => alive && setImg(t)).catch(() => setImg(null));
        return () => { alive = false; };
    }, [tileset.uuid, tileset.texture_asset_uuid]);

    // Redraw the atlas + grid + selection.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        canvas.width = cols * tw * zoom;
        canvas.height = rows * th * zoom;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw each tile through the shared slicing math.
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const idx = r * cols + c;
                if (tileset.tile_count != null && idx >= tileset.tile_count) continue;
                const src = img.tileRectPx(idx);
                ctx.drawImage(img.image, src.x, src.y, src.w, src.h, c * tw * zoom, r * th * zoom, tw * zoom, th * zoom);
            }
        }

        // Grid.
        ctx.strokeStyle = 'rgba(128,128,128,0.35)';
        ctx.lineWidth = 1;
        for (let c = 0; c <= cols; c++) line(ctx, c * tw * zoom + 0.5, 0, c * tw * zoom + 0.5, canvas.height);
        for (let r = 0; r <= rows; r++) line(ctx, 0, r * th * zoom + 0.5, canvas.width, r * th * zoom + 0.5);

        // Selection outline.
        if (sel) {
            const x = Math.min(sel.c0, sel.c1) * tw * zoom;
            const y = Math.min(sel.r0, sel.r1) * th * zoom;
            const w = (Math.abs(sel.c1 - sel.c0) + 1) * tw * zoom;
            const h = (Math.abs(sel.r1 - sel.r0) + 1) * th * zoom;
            ctx.strokeStyle = ORANGE;
            ctx.lineWidth = 2;
            ctx.strokeRect(x + 1, y + 1, w - 2, h - 2);
        }
    }, [img, zoom, cols, rows, tw, th, sel, tileset.tile_count]);

    const cellFromEvent = (e: React.PointerEvent) => {
        const rect = canvasRef.current!.getBoundingClientRect();
        const c = Math.floor((e.clientX - rect.left) / (tw * zoom));
        const r = Math.floor((e.clientY - rect.top) / (th * zoom));
        return { c: Math.max(0, Math.min(cols - 1, c)), r: Math.max(0, Math.min(rows - 1, r)) };
    };

    const commitStamp = (s: { c0: number; r0: number; c1: number; r1: number }) => {
        const cMin = Math.min(s.c0, s.c1), cMax = Math.max(s.c0, s.c1);
        const rMin = Math.min(s.r0, s.r1), rMax = Math.max(s.r0, s.r1);
        const w = cMax - cMin + 1, h = rMax - rMin + 1;
        const tiles: number[] = [];
        for (let r = rMin; r <= rMax; r++) {
            for (let c = cMin; c <= cMax; c++) {
                const idx = r * cols + c;
                tiles.push(idx + 1); // tile id = atlas index + 1
            }
        }
        tilemapStore.getState().setStamp({ w, h, tiles });
    };

    return (
        <div className="d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="d-flex align-items-center gap-2 px-2 py-1 small text-secondary">
                <span>{tileset.name}</span>
                <span className="ms-auto">Zoom</span>
                <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setZoom((z) => Math.max(1, z - 1))}>−</button>
                <span>{zoom}×</span>
                <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setZoom((z) => Math.min(8, z + 1))}>+</button>
            </div>
            <div className="overflow-auto p-2" style={{ minHeight: 0 }}>
                <canvas
                    ref={canvasRef}
                    style={{ imageRendering: 'pixelated', cursor: 'crosshair', display: 'block' }}
                    onPointerDown={(e) => {
                        dragging.current = true;
                        const { c, r } = cellFromEvent(e);
                        setSel({ c0: c, r0: r, c1: c, r1: r });
                        (e.target as HTMLElement).setPointerCapture(e.pointerId);
                    }}
                    onPointerMove={(e) => {
                        if (!dragging.current || !sel) return;
                        const { c, r } = cellFromEvent(e);
                        setSel((s) => (s ? { ...s, c1: c, r1: r } : s));
                    }}
                    onPointerUp={() => {
                        dragging.current = false;
                        if (sel) commitStamp(sel);
                    }}
                />
            </div>

            {sel && (
                <TileMetaStrip
                    projectUuid={projectUuid}
                    tileset={tileset}
                    atlasIndex={Math.min(sel.r0, sel.r1) * cols + Math.min(sel.c0, sel.c1)}
                />
            )}
            <div className="px-2 py-1 small text-secondary border-top">
                Stamp {stamp.w}×{stamp.h} · click a tile or drag to select a block
            </div>
        </div>
    );
}

/**
 * Solid/Tag/Animation editor for the top-left selected tile, writing through
 * to the tileset. Every save sends the tile's FULL current entry (solid + tag
 * + animation), not a partial diff — the backend's tile-meta patch replaces
 * a tile's whole entry per index (doc 03 §6), so omitting a field here would
 * silently delete it, the same class of bug fixed for connections'
 * source_pin/target_pin (see GraphContentPersistenceTest.php).
 */
function TileMetaStrip({ projectUuid, tileset, atlasIndex }: { projectUuid: string; tileset: Tileset; atlasIndex: number }) {
    const key = String(atlasIndex);
    const meta = tileset.tile_meta?.[key] ?? {};
    const [solid, setSolid] = useState<boolean>(!!meta.solid);
    const [tag, setTag] = useState<string>(meta.tag ?? '');
    const [animated, setAnimated] = useState<boolean>(!!meta.animation);
    const [frames, setFrames] = useState<number[]>(meta.animation?.frames ?? [atlasIndex, atlasIndex]);
    const [frameDurationMs, setFrameDurationMs] = useState<number>(meta.animation?.frameDurationMs ?? 200);
    const [showFramePicker, setShowFramePicker] = useState(false);

    // Reset the local fields whenever the selected tile changes.
    useEffect(() => {
        const m = tileset.tile_meta?.[key];
        setSolid(!!m?.solid);
        setTag(m?.tag ?? '');
        setAnimated(!!m?.animation);
        setFrames(m?.animation?.frames ?? [atlasIndex, atlasIndex]);
        setFrameDurationMs(m?.animation?.frameDurationMs ?? 200);
        setShowFramePicker(false);
    }, [key, tileset.tile_meta, atlasIndex]);

    const save = (next: { solid?: boolean; tag?: string; animation?: { frames: number[]; frameDurationMs: number } | null }) => {
        const mergedSolid = next.solid ?? solid;
        const mergedTag = next.tag ?? tag;
        const mergedAnimation = next.animation !== undefined ? next.animation : (animated ? { frames, frameDurationMs } : null);
        const empty = !mergedSolid && !mergedTag && !mergedAnimation;
        const payload = empty ? null : {
            solid: mergedSolid,
            tag: mergedTag || undefined,
            animation: mergedAnimation ?? undefined,
        };
        void tilemapClient.updateTileMeta(projectUuid, tileset.uuid, { [key]: payload }).then(() => {
            void tilemapStore.getState().reload();
        });
    };

    const toggleAnimated = (checked: boolean) => {
        setAnimated(checked);
        if (checked) {
            const initial = frames.length >= 2 ? frames : [atlasIndex, atlasIndex];
            setFrames(initial);
            save({ animation: { frames: initial, frameDurationMs } });
        } else {
            save({ animation: null });
        }
    };

    const addFrame = (idx: number) => {
        const next = [...frames, idx];
        setFrames(next);
        save({ animation: { frames: next, frameDurationMs } });
    };

    const removeFrame = (i: number) => {
        if (frames.length <= 2) return; // backend requires at least 2 frames
        const next = frames.filter((_, fi) => fi !== i);
        setFrames(next);
        save({ animation: { frames: next, frameDurationMs } });
    };

    return (
        <div className="border-top small">
            <div className="d-flex align-items-center gap-2 px-2 py-1">
                <span className="text-secondary">Tile {atlasIndex}</span>
                <label className="form-check form-check-inline mb-0">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        checked={solid}
                        onChange={(e) => { setSolid(e.target.checked); save({ solid: e.target.checked }); }}
                    />
                    <span className="form-check-label">Solid</span>
                </label>
                <input
                    className="form-control form-control-sm"
                    style={{ width: 100 }}
                    placeholder="tag"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    onBlur={() => save({ tag })}
                />
                <label className="form-check form-check-inline mb-0" title="Cycle through frames when viewed in the editor (preview only — not exported to the engine yet)">
                    <input type="checkbox" className="form-check-input" checked={animated} onChange={(e) => toggleAnimated(e.target.checked)} />
                    <span className="form-check-label">Animated</span>
                </label>
            </div>

            {animated && (
                <div className="px-2 pb-2">
                    <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                        <span className="text-secondary">Frames:</span>
                        {frames.map((f, i) => (
                            <span key={i} className="badge bg-secondary-lt d-flex align-items-center gap-1">
                                {f}
                                {frames.length > 2 && (
                                    <button className="btn-close" style={{ fontSize: 8 }} onClick={() => removeFrame(i)} title="Remove frame" />
                                )}
                            </span>
                        ))}
                        <button className="btn btn-sm btn-outline-primary py-0" onClick={() => setShowFramePicker((v) => !v)}>
                            + Frame
                        </button>
                    </div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="text-secondary">Duration</span>
                        <input
                            type="number"
                            min={16}
                            max={60000}
                            className="form-control form-control-sm"
                            style={{ width: 90 }}
                            value={frameDurationMs}
                            onChange={(e) => setFrameDurationMs(Number(e.target.value))}
                            onBlur={() => save({ animation: { frames, frameDurationMs } })}
                        />
                        <span className="text-secondary">ms/frame</span>
                    </div>
                    {showFramePicker && (
                        <MiniAtlasPicker tileset={tileset} onPick={(idx) => { addFrame(idx); setShowFramePicker(false); }} />
                    )}
                </div>
            )}
        </div>
    );
}

/** A click-to-pick-one atlas grid, used to append an animation frame. */
function MiniAtlasPicker({ tileset, onPick }: { tileset: Tileset; onPick: (atlasIndex: number) => void }) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [img, setImg] = useState<TilesetImage | null>(null);
    const zoom = 2;
    const cols = tileset.columns ?? 1;
    const rows = tileset.tile_count && cols ? Math.ceil(tileset.tile_count / cols) : 1;
    const tw = tileset.tile_width;
    const th = tileset.tile_height;

    useEffect(() => {
        let alive = true;
        TilesetImage.load(tileset).then((t) => alive && setImg(t)).catch(() => setImg(null));
        return () => { alive = false; };
    }, [tileset.uuid, tileset.texture_asset_uuid]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        canvas.width = cols * tw * zoom;
        canvas.height = rows * th * zoom;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const idx = r * cols + c;
                if (tileset.tile_count != null && idx >= tileset.tile_count) continue;
                const src = img.tileRectPx(idx);
                ctx.drawImage(img.image, src.x, src.y, src.w, src.h, c * tw * zoom, r * th * zoom, tw * zoom, th * zoom);
            }
        }
    }, [img, cols, rows, tw, th, tileset.tile_count]);

    return (
        <canvas
            ref={canvasRef}
            style={{ imageRendering: 'pixelated', cursor: 'pointer', maxWidth: '100%', display: 'block', border: '1px solid var(--tblr-border-color)' }}
            onClick={(e) => {
                const rect = canvasRef.current!.getBoundingClientRect();
                const c = Math.floor((e.clientX - rect.left) / (tw * zoom));
                const r = Math.floor((e.clientY - rect.top) / (th * zoom));
                onPick(r * cols + c);
            }}
        />
    );
}

function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number) {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
}
